import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  HiTag,
  HiOfficeBuilding,
  HiDocumentText,
  HiPencilAlt,
  HiUpload,
  HiTruck,
  HiLocationMarker,
  HiCollection,
  HiTable,
  HiX,
  HiBookmark,
  HiSave,
  HiTrash,
  HiDownload,
} from "react-icons/hi";
import { useSelector } from "react-redux";
import { useGetMyEntreprisesQuery } from "../../slices/entrepriseApiSlice";
import { selectGlobalEntrepriseId } from "../../slices/entrepriseGlobalSlice";
import {
  useGetGism1Query,
  useGetGismLevelQuery,
  useGetGroupesQuery,
} from "../../slices/articleApiSlice";
import {
  useGetEtiquetteTemplatesQuery,
  useCreateEtiquetteTemplateMutation,
  useDeleteEtiquetteTemplateMutation,
} from "../../slices/etiquetteTemplateApiSlice";
import { BASE_URL } from "../../constants";
// Sélecteur multiple recherchable : mis au point ici, désormais PARTAGÉ
// (les demandes de bipage s'en servent pour les mêmes listes gisement/groupe).
import SelecteurMultiple from "../../components/common/SelecteurMultiple/SelecteurMultiple";
import CustomEtiquetteDesigner from "../../components/Admin/CustomEtiquetteDesigner";
import "./AdminEtiquettesScreen.css";

// Types d'étiquettes (clés alignées avec le backend etiquetteService.js)
const LABEL_TYPES = [
  {
    type: "standard",
    title: "🏷️ Standard",
    desc: "Petite étiquette rayonnage (5 × 4 cm, plusieurs par page).",
  },
  {
    type: "standard_sans_prix",
    title: "🏷️ Standard sans prix",
    desc: "Même format que Standard (5 × 4 cm, plusieurs par page), mais sans le prix.",
  },
  {
    type: "standard_promo",
    title: "🔥 Standard promo",
    desc: "Grille 5 × 4 cm : bandeau PROMO, prix promo, ancien prix barré et dates de la promo.",
  },
  {
    type: "standard_solde",
    title: "💥 Standard solde",
    desc: "Grille 5 × 4 cm : bandeau SOLDE, prix promo, ancien prix barré et dates.",
  },
  {
    type: "standard_destockage",
    title: "📦 Standard déstockage",
    desc: "Grille 5 × 4 cm : bandeau DESTOCKAGE, prix promo et « jusqu'à épuisement du stock ».",
  },
  {
    type: "promo",
    title: "🔥 Promotion",
    desc: "Pleine page A4 avec prix barré, prix promo et dates.",
  },
  {
    type: "solde",
    title: "💥 Solde",
    desc: "Pleine page A4 pour les articles en solde.",
  },
  {
    type: "destockage",
    title: "📦 Destockage",
    desc: "Pleine page A4, jusqu'à épuisement du stock.",
  },
  {
    type: "sans_prix",
    title: "🏪 Sans prix",
    desc: "Pleine page A4 sans prix (vitrine / présentation).",
  },
  {
    type: "normal",
    title: "📋 Normal",
    desc: "Pleine page A4 avec prix et code-barres.",
  },
  {
    type: "inventaire",
    title: "🔢 Inventaire",
    desc: "NART et désignation en gros + grande case « Quantité » à remplir au stylo (A4 / demi A4).",
  },
  {
    type: "qr_gisement",
    title: "📍 QR gisement (gondole)",
    desc:
      "Panneau de rayon, pas étiquette d'article : un QR d'environ 5 cm par " +
      "gisement (12 par feuille A4), à afficher en gondole.",
  },
  {
    type: "custom",
    title: "🎨 Personnalisée",
    desc: "Vous choisissez la taille (cm/px), écrivez et placez votre texte. Plusieurs par feuille A4 si ça rentre.",
  },
];

// Grilles 5 × 4 cm AVEC prix : les seules à laisser la zone code-barres VIDE
// quand l'article n'a pas de GENCOD exploitable — donc les seules pour
// lesquelles on pose la question « NART à la place ou vide ? » avant de générer.
const TYPES_GRILLE_AVEC_PRIX = [
  "standard",
  "standard_promo",
  "standard_solde",
  "standard_destockage",
];

const AdminEtiquettesScreen = () => {
  const { data: entreprises, isLoading: loadingEntreprises } =
    useGetMyEntreprisesQuery();

  // Société active : lue depuis la sélection GLOBALE (Header).
  const globalEntrepriseId = useSelector(selectGlobalEntrepriseId);
  const selectedEntreprise = globalEntrepriseId || "";
  const [mode, setMode] = useState("proforma"); // proforma | commande | nart | gism1
  const [numfact, setNumfact] = useState("");
  const [numcde, setNumcde] = useState("");
  const [nartText, setNartText] = useState("");
  const [selectedGism1, setSelectedGism1] = useState([]); // liste de codes GISM1
  const [selectedGroupe, setSelectedGroupe] = useState([]); // liste de codes GROUPE
  // Panneaux QR de gisement : l'emplacement choisit le NIVEAU lu sur la fiche
  // article — MAGASIN = GISM1 (le rayon), DOCK = GISM2 (la réserve), TOUS = les
  // deux planches dans un seul PDF. Ce sont deux jeux de codes différents : la
  // sélection est remise à zéro quand l'emplacement change, et « TOUS » ne
  // propose pas de sélection du tout (elle n'aurait pas de sens).
  const [emplacementQr, setEmplacementQr] = useState("MAGASIN");
  const [selectedGisQr, setSelectedGisQr] = useState([]);
  // L'export Excel a SON PROPRE choix d'emplacement, indépendant de celui des
  // panneaux : on imprime souvent les panneaux d'un seul emplacement alors
  // qu'on veut le comptage des deux (ou l'inverse).
  const [emplacementExcel, setEmplacementExcel] = useState("MAGASIN");
  const [excelGisLoading, setExcelGisLoading] = useState(false);
  const [type, setType] = useState("standard");
  const [format, setFormat] = useState("a4"); // a4 | demi (types pleine page)
  const [customLayout, setCustomLayout] = useState(null); // layout du designer custom
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [csvCount, setCsvCount] = useState(0);
  const fileInputRef = useRef(null);

  // Contrôle « articles sans code-barres » : renseigné juste avant la
  // génération quand au moins un article sélectionné n'a pas de GENCOD
  // exploitable. Porte la réponse du serveur ET le corps de requête déjà
  // construit, pour relancer la génération telle quelle après le choix.
  const [controleGencod, setControleGencod] = useState(null);

  // Import « en masse » (type custom) : fichier Excel/CSV SANS en-tête. Chaque
  // ligne = une étiquette ; les colonnes deviennent des champs plaçables (imp0…N).
  const [importRows, setImportRows] = useState([]); // tableau de lignes (tableaux de cellules)
  const [importFileName, setImportFileName] = useState("");
  const [importError, setImportError] = useState("");
  const importFileRef = useRef(null);

  // Nombre de colonnes détecté = ligne la plus large.
  const importCols = importRows.reduce((m, r) => Math.max(m, r.length), 0);
  const importFields = Array.from({ length: importCols }, (_, i) => ({
    key: `imp${i}`,
    label: `Colonne ${i + 1}`,
  }));

  // Templates d'étiquettes personnalisées (partagés par société).
  const [designerKey, setDesignerKey] = useState(0); // remonte le designer au chargement
  const [templateInitial, setTemplateInitial] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [tplMsg, setTplMsg] = useState(null);

  // Détecte le séparateur le plus fréquent et extrait la 1re colonne de chaque ligne.
  const parseCsvNarts = (text) => {
    const content = String(text).replace(/^\uFEFF/, ""); // BOM éventuel
    const lines = content.split(/\r\n|\r|\n/).filter((l) => l.trim() !== "");
    if (lines.length === 0) return [];

    const candidates = [";", ",", "\t", "|"];
    let sep = null;
    let best = 0;
    for (const c of candidates) {
      const count = (content.match(new RegExp(`\\${c}`, "g")) || []).length;
      if (count > best) { best = count; sep = c; }
    }

    const clean = (cell) =>
      String(cell).trim().replace(/^["']+|["']+$/g, "").trim();

    const narts = lines.map((line) => {
      const first = sep ? line.split(sep)[0] : line;
      return clean(first);
    });

    // Ignorer une éventuelle ligne d'en-tête (ex. "NART", "code", "reference")
    if (narts.length && /^(nart|code|r[ée]f[ée]rence|ref)$/i.test(narts[0])) {
      narts.shift();
    }
    return narts.filter(Boolean);
  };

  const handleCsvImport = (e) => {
    setError("");
    setInfo(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const narts = parseCsvNarts(ev.target.result);
        if (narts.length === 0) {
          setError("Aucun NART trouvé dans le fichier.");
          setCsvCount(0);
        } else {
          setNartText(narts.join("\n"));
          setCsvCount(narts.length);
          setInfo(`${narts.length} NART importé(s) depuis « ${file.name} ».`);
        }
      } catch {
        setError("Fichier illisible ou format non reconnu.");
      }
    };
    reader.onerror = () => setError("Impossible de lire le fichier.");
    reader.readAsText(file);
    // Réinitialise pour permettre de réimporter le même fichier
    e.target.value = "";
  };

  // Import « en masse » : lit un fichier Excel/CSV SANS en-tête en tableau de
  // lignes (chaque ligne = tableau de cellules texte). Colonnes vides finales
  // ignorées ; détecte le nombre de colonnes = ligne la plus large.
  const handleImportFile = (e) => {
    setImportError("");
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        // header:1 → tableau de tableaux ; blankrows:false → ignore lignes vides.
        const aoa = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          blankrows: false,
          defval: "",
        });
        const rows = aoa
          .map((r) => (Array.isArray(r) ? r.map((c) => (c == null ? "" : String(c).trim())) : []))
          .filter((r) => r.some((c) => c !== ""));
        if (rows.length === 0) {
          setImportRows([]);
          setImportFileName("");
          setImportError("Aucune donnée trouvée dans le fichier.");
          return;
        }
        setImportRows(rows);
        setImportFileName(file.name);
      } catch {
        setImportRows([]);
        setImportFileName("");
        setImportError("Fichier illisible (formats acceptés : .xlsx, .xls, .csv).");
      }
    };
    reader.onerror = () =>
      setImportError("Impossible de lire le fichier.");
    reader.readAsArrayBuffer(file);
    e.target.value = ""; // permet de réimporter le même fichier
  };

  const clearImport = () => {
    setImportRows([]);
    setImportFileName("");
    setImportError("");
  };

  const entrepriseData = entreprises?.find((e) => e._id === selectedEntreprise);
  const nomDossierDBF = entrepriseData?.nomDossierDBF;

  // Listes GISM1 / GROUPE de l'entreprise (chargées à la demande, cache 5 min)
  // `vide: true` ajoute en tete de liste l'entree « VIDE » = les articles sans
  // gisement / sans groupe, qu'aucune selection ne permettait d'atteindre.
  const { data: gism1Data, isLoading: loadingGism1 } = useGetGism1Query(
    { nomDossierDBF, vide: true },
    { skip: !nomDossierDBF || mode !== "gism1" },
  );
  const { data: groupeData, isLoading: loadingGroupe } = useGetGroupesQuery(
    { nomDossierDBF, vide: true },
    { skip: !nomDossierDBF || mode !== "groupe" },
  );

  // Codes du niveau de gisement visé par les panneaux QR (1 = GISM1 magasin,
  // 2 = GISM2 dock). Pas d'entrée « VIDE » ici : un gisement sans code n'a pas
  // de panneau à afficher. En mode TOUS il n'y a rien à sélectionner, donc
  // rien à charger.
  const niveauQr = emplacementQr === "DOCK" ? 2 : 1;
  const { data: gisQrData, isLoading: loadingGisQr } = useGetGismLevelQuery(
    { nomDossierDBF, niveau: niveauQr },
    {
      skip:
        !nomDossierDBF || type !== "qr_gisement" || emplacementQr === "TOUS",
    },
  );

  // Templates de la société (visibles/utilisables par tous les users y ayant accès).
  const { data: templates = [] } = useGetEtiquetteTemplatesQuery(nomDossierDBF, {
    skip: !nomDossierDBF,
  });
  const [createTemplate, { isLoading: savingTpl }] =
    useCreateEtiquetteTemplateMutation();
  const [deleteTemplate] = useDeleteEtiquetteTemplateMutation();

  const saveTemplate = async () => {
    setTplMsg(null);
    if (!nomDossierDBF) return setTplMsg({ type: "err", text: "Sélectionnez une société." });
    if (!templateName.trim()) return setTplMsg({ type: "err", text: "Donnez un nom au template." });
    if (!customLayout || !(customLayout.elements || []).length)
      return setTplMsg({ type: "err", text: "Ajoutez au moins un élément avant d'enregistrer." });
    try {
      await createTemplate({
        nomDossierDBF,
        nom: templateName.trim(),
        dataMode: mode !== "aucun",
        config: {
          widthPx: customLayout.widthPx,
          heightPx: customLayout.heightPx,
          copies: customLayout.copies,
          elements: customLayout.elements,
        },
      }).unwrap();
      setTemplateName("");
      setTplMsg({ type: "ok", text: "Template enregistré (visible par toute la société)." });
    } catch (e) {
      setTplMsg({ type: "err", text: e?.data?.message || e.message });
    }
  };

  const loadTemplate = (tpl) => {
    const cfg = tpl.config || {};
    setType("custom");
    // Template « import » (colonnes libres imp0…N) : rebascule en mode import ;
    // l'utilisateur (re)fournit le fichier de données.
    const isImportTpl = (cfg.elements || []).some((el) =>
      /^imp\d+$/.test(el?.field || ""),
    );
    if (isImportTpl) setMode("import");
    else setMode(tpl.dataMode ? (mode === "aucun" || mode === "import" ? "nart" : mode) : "aucun");
    setTemplateInitial({
      unit: "px",
      width: cfg.widthPx,
      height: cfg.heightPx,
      copies: cfg.copies || 1,
      elements: cfg.elements || [],
    });
    setDesignerKey((k) => k + 1); // force le remontage du designer avec `initial`
    setTplMsg({ type: "ok", text: `Template « ${tpl.nom} » chargé.` });
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(`Supprimer le template « ${tpl.nom} » ?`)) return;
    try {
      await deleteTemplate({ nomDossierDBF, id: tpl._id }).unwrap();
    } catch (e) {
      alert(e?.data?.message || "Suppression impossible");
    }
  };

  // Réinitialise les sélections si on change d'entreprise
  useEffect(() => {
    setSelectedGism1([]);
    setSelectedGroupe([]);
    setSelectedGisQr([]);
  }, [nomDossierDBF]);

  // Les codes GISM1 et GISM2 n'ont rien à voir : garder la sélection en
  // changeant d'emplacement enverrait des codes qui n'existent pas au dock.
  useEffect(() => {
    setSelectedGisQr([]);
  }, [emplacementQr]);

  const toggleGism1 = (code) => {
    setSelectedGism1((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleGisQr = (code) => {
    setSelectedGisQr((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleGroupe = (code) => {
    setSelectedGroupe((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  // Valide le mode « source article » courant et renvoie les champs à envoyer.
  const buildArticleSource = () => {
    if (mode === "proforma") {
      if (!numfact.trim())
        return { ok: false, error: "Saisissez un numéro de proforma." };
      return { ok: true, fields: { numfact: numfact.trim() } };
    }
    if (mode === "commande") {
      if (!numcde.trim())
        return { ok: false, error: "Saisissez un numéro de commande." };
      return { ok: true, fields: { numcde: numcde.trim() } };
    }
    if (mode === "nart") {
      const narts = nartText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (narts.length === 0)
        return { ok: false, error: "Saisissez au moins un NART." };
      return { ok: true, fields: { narts } };
    }
    if (mode === "gism1") {
      if (selectedGism1.length === 0)
        return { ok: false, error: "Sélectionnez au moins un gisement (GISM1)." };
      return { ok: true, fields: { gism1: selectedGism1 } };
    }
    if (mode === "groupe") {
      if (selectedGroupe.length === 0)
        return { ok: false, error: "Sélectionnez au moins un groupe (GROUPE)." };
      return { ok: true, fields: { groupe: selectedGroupe } };
    }
    if (mode === "import") {
      if (importRows.length === 0)
        return { ok: false, error: "Importez un fichier Excel/CSV (une ligne = une étiquette)." };
      return { ok: true, fields: { rows: importRows } };
    }
    return { ok: false, error: "Choisissez une source d'articles." };
  };

  // Demande au serveur quels articles de la sélection n'ont pas de code-barres
  // imprimable. Best effort : si le contrôle échoue (source invalide, réseau),
  // on renvoie null et la génération suit son cours — c'est elle qui portera
  // le vrai message d'erreur.
  const controlerArticlesSansGencod = async (body) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/etiquettes/${nomDossierDBF}/controle-gencod`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  };

  const genererEtiquettes = async () => {
    setError("");
    setInfo(null);
    setControleGencod(null);

    if (!nomDossierDBF) {
      setError("Sélectionnez une entreprise.");
      return;
    }

    const customBody =
      type === "custom"
        ? {
            type: "custom",
            layout: {
              widthPx: customLayout?.widthPx,
              heightPx: customLayout?.heightPx,
              border: true,
              elements: customLayout?.elements || [],
            },
          }
        : null;

    let body;
    if (type === "qr_gisement") {
      // Panneau de rayon : ni article, ni prix, ni code-barres — donc aucune
      // source à valider et aucun contrôle GENCOD à poser. Une sélection vide
      // vaut « tous les gisements du niveau » (le serveur les énumère).
      body = {
        type,
        emplacement: emplacementQr,
        codes: selectedGisQr,
      };
      await lancerGeneration(body);
      return;
    }
    if (type === "custom" && mode === "aucun") {
      if (!customLayout || !(customLayout.elements || []).length) {
        setError("Ajoutez au moins un élément à l'étiquette personnalisée.");
        return;
      }
      body = { ...customBody, copies: Math.max(1, Number(customLayout.copies) || 1) };
    } else {
      // Source article (proforma / commande / nart / gism1 / groupe).
      const src = buildArticleSource();
      if (!src.ok) {
        setError(src.error);
        return;
      }
      if (type === "custom") {
        if (!customLayout || !(customLayout.elements || []).length) {
          setError("Ajoutez au moins un élément à l'étiquette personnalisée.");
          return;
        }
        body = { ...customBody, mode, ...src.fields };
      } else {
        body = { type, mode, ...src.fields, format };
      }
    }

    // Seules les grilles AVEC prix (Standard et ses variantes promo / solde /
    // déstockage) laissent la zone code-barres VIDE quand l'article n'a pas de
    // GENCOD. On ne pose donc la question que là : « Standard sans prix »
    // affiche déjà le NART en gros au centre, les formats pleine page (promo,
    // solde, déstockage, sans prix, normal) impriment désormais le NART À LA
    // PLACE du code-barres absent, et le type custom n'a aucune zone imposée.
    if (TYPES_GRILLE_AVEC_PRIX.includes(type) && mode !== "aucun" && mode !== "import") {
      const controle = await controlerArticlesSansGencod(body);
      if (controle && controle.nbSansGencod > 0) {
        // On ne génère rien tant que l'utilisateur n'a pas tranché.
        setControleGencod({ ...controle, body });
        return;
      }
    }

    await lancerGeneration(body);
  };

  // Génération réelle. `sansGencod` = choix de la fenêtre de contrôle
  // (« nart » / « vide »), absent quand la question ne s'est pas posée.
  const lancerGeneration = async (bodyBase, sansGencod) => {
    setError("");
    setInfo(null);
    setControleGencod(null);
    const body = sansGencod ? { ...bodyBase, sansGencod } : bodyBase;

    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/etiquettes/${nomDossierDBF}/generer`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      if (!res.ok) {
        let msg = `Génération échouée (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {
          /* réponse non-JSON */
        }
        throw new Error(msg);
      }

      const total = res.headers.get("X-Articles-Total");
      const trouves = res.headers.get("X-Articles-Trouves");
      const introuvables = res.headers.get("X-Articles-Introuvables");

      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download =
        type === "qr_gisement"
          ? `qr_gisements_${emplacementQr.toLowerCase()}.pdf` // magasin | dock | tous
          : `etiquettes_${type}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);

      let note;
      if (type === "qr_gisement") {
        // Ce type ne compte pas des articles mais des panneaux : l'entête est
        // X-Etiquettes, pas X-Articles-Trouves.
        const nb = res.headers.get("X-Etiquettes");
        const ou =
          emplacementQr === "TOUS"
            ? "le magasin (GISM1) puis le dock (GISM2)"
            : emplacementQr === "DOCK"
              ? "le dock (GISM2)"
              : "le magasin (GISM1)";
        note = `${nb || "?"} panneau(x) QR généré(s) pour ${ou} — 12 par feuille A4.`;
      } else if (type === "custom") {
        if (mode === "import") {
          note = `PDF généré (${trouves || importRows.length} étiquette(s) depuis le fichier importé).`;
        } else if (trouves && Number(trouves) > 0) {
          note = `PDF généré (${trouves} étiquette(s) depuis les articles).`;
        } else {
          note = `PDF généré (${body.copies || "?"} étiquette(s) personnalisée(s)).`;
        }
      } else {
        note = `${trouves || "?"} étiquette(s) générée(s).`;
        if (introuvables && Number(introuvables) > 0) {
          note += ` ${introuvables} NART introuvable(s) sur ${total} ignoré(s).`;
        }
      }
      setInfo(note);
    } catch (e) {
      setError(e.message || "Impossible de générer les étiquettes");
    } finally {
      setLoading(false);
    }
  };

  // La sélection de codes ne s'applique au comptage que si l'export porte sur
  // le MÊME emplacement que les panneaux : les codes cochés sont ceux d'un
  // niveau précis (GISM1 ou GISM2), les transposer à l'autre n'aurait aucun sens.
  const excelSuitSelection =
    emplacementExcel !== "TOUS" &&
    emplacementExcel === emplacementQr &&
    selectedGisQr.length > 0;

  // Comptage des gisements au format Excel (gisement | nb d'articles | libellé).
  const telechargerExcelGisements = async () => {
    setError("");
    setInfo(null);
    if (!nomDossierDBF) {
      setError("Sélectionnez une entreprise.");
      return;
    }

    const params = new URLSearchParams({ emplacement: emplacementExcel });
    if (excelSuitSelection)
      selectedGisQr.forEach((c) => params.append("codes", c));

    setExcelGisLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/etiquettes/${nomDossierDBF}/gisements-excel?${params}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        let msg = `Export échoué (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {
          /* réponse non-JSON */
        }
        throw new Error(msg);
      }

      const nb = res.headers.get("X-Gisements");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `gisements_nb_articles_${emplacementExcel.toLowerCase()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
      setInfo(
        `Excel généré : ${nb || "?"} gisement(s)` +
          (emplacementExcel === "TOUS"
            ? " sur deux feuilles (GISM1 magasin, GISM2 dock)."
            : "."),
      );
    } catch (e) {
      setError(e.message || "Impossible de générer l'export Excel");
    } finally {
      setExcelGisLoading(false);
    }
  };

  if (loadingEntreprises) {
    return (
      <div className="etiq-screen">
        <div className="etiq-placeholder">Chargement des entreprises…</div>
      </div>
    );
  }

  if (!entreprises || entreprises.length === 0) {
    return (
      <div className="etiq-screen">
        <div className="etiq-placeholder">
          Vous n'avez accès à aucune entreprise.
        </div>
      </div>
    );
  }

  return (
    <div className={`etiq-screen ${type === "custom" ? "etiq-screen--wide" : ""}`}>
      <div className="etiq-header">
        <h1>
          <HiTag /> Générateur d'étiquettes
        </h1>
        <p>
          Générez vos étiquettes PDF (code-barres EAN-13) depuis une proforma,
          une commande, une liste de NART, un gisement (GISM1) ou un groupe
          (GROUPE) — ou les panneaux <strong>QR de gisement</strong> à afficher
          en gondole.
        </p>
      </div>

      {/* Société : choisie globalement dans l'en-tête */}
      {!selectedEntreprise && (
        <div className="etiq-card">
          <p className="etiq-hint">
            <HiOfficeBuilding /> Sélectionnez une société dans l'en-tête pour
            générer des étiquettes.
          </p>
        </div>
      )}

      {/* Panneaux QR de gisement : il n'y a pas de source d'articles à choisir,
          ce bloc remplace celui des sources. */}
      {type === "qr_gisement" && (
        <div className="etiq-card">
          <label className="etiq-label">Emplacement</label>
          <div className="etiq-mode-tabs">
            <button
              type="button"
              className={`etiq-mode-btn ${emplacementQr === "MAGASIN" ? "active" : ""}`}
              onClick={() => setEmplacementQr("MAGASIN")}
            >
              <HiLocationMarker /> Magasin (GISM1)
            </button>
            <button
              type="button"
              className={`etiq-mode-btn ${emplacementQr === "DOCK" ? "active" : ""}`}
              onClick={() => setEmplacementQr("DOCK")}
            >
              <HiTruck /> Dock / réserve (GISM2)
            </button>
            <button
              type="button"
              className={`etiq-mode-btn ${emplacementQr === "TOUS" ? "active" : ""}`}
              onClick={() => setEmplacementQr("TOUS")}
            >
              <HiCollection /> Tout générer (les deux)
            </button>
          </div>
          <span className="etiq-hint">
            {emplacementQr === "MAGASIN"
              ? "Un panneau par gisement GISM1 — le rayon où l'article est présenté."
              : emplacementQr === "DOCK"
                ? "Un panneau par gisement GISM2 — l'emplacement de réserve au dock."
                : "Les deux planches dans un seul PDF : tous les GISM1 (magasin) puis tous les GISM2 (dock)."}{" "}
            Le QR encode le <strong>code du gisement</strong> (ce que lit la
            douchette et l&apos;app collecteur) ; le libellé imprimé vient du
            dictionnaire des rayons, lu pour <strong>cet emplacement</strong> —
            un même code peut porter deux noms différents au magasin et au dock.
          </span>

          {/* « Tout générer » ne propose aucune sélection : les deux niveaux
              n'ont pas les mêmes codes, cocher des cases n'aurait pas de sens. */}
          {emplacementQr === "TOUS" ? (
            <span className="etiq-hint">
              Chaque emplacement <strong>démarre sur une nouvelle feuille</strong>{" "}
              et son pied de page rappelle lequel c&apos;est : une fois les
              panneaux découpés, un code dock reste distinguable d&apos;un code
              magasin. Pour ne tirer qu&apos;une partie des rayons, choisissez
              un emplacement précis ci-dessus.
            </span>
          ) : (
            <div className="etiq-field">
              <label className="etiq-label">
                Gisement(s) GISM{niveauQr}
              </label>
              <SelecteurMultiple
                key={`qrgis-${nomDossierDBF || ""}-${emplacementQr}`}
                items={gisQrData?.gisements}
                selected={selectedGisQr}
                onToggle={toggleGisQr}
                onClear={() => setSelectedGisQr([])}
                loading={loadingGisQr}
                placeholder="Rechercher et sélectionner un ou plusieurs gisements…"
              />
              <span className="etiq-hint">
                {selectedGisQr.length > 0
                  ? `${selectedGisQr.length} panneau(x) seront générés, rangés dans l'ordre naturel des codes (A_1, A_2d, A_9, A_10…).`
                  : `Aucune sélection : un panneau sera généré pour TOUS les gisements GISM${niveauQr} de la société${
                      gisQrData?.total ? ` (${gisQrData.total} codes)` : ""
                    }.`}
              </span>
            </div>
          )}

          {/* Comptage en Excel — choix d'emplacement INDÉPENDANT de celui des
              panneaux (on veut souvent le comptage des deux niveaux même en
              n'imprimant qu'une planche). */}
          <div className="etiq-field">
            <label className="etiq-label">
              <HiDownload /> Excel : gisements et nombre d&apos;articles
            </label>
            <div className="etiq-mode-tabs">
              <button
                type="button"
                className={`etiq-mode-btn ${emplacementExcel === "MAGASIN" ? "active" : ""}`}
                onClick={() => setEmplacementExcel("MAGASIN")}
              >
                GISM1 — magasin
              </button>
              <button
                type="button"
                className={`etiq-mode-btn ${emplacementExcel === "DOCK" ? "active" : ""}`}
                onClick={() => setEmplacementExcel("DOCK")}
              >
                GISM2 — dock
              </button>
              <button
                type="button"
                className={`etiq-mode-btn ${emplacementExcel === "TOUS" ? "active" : ""}`}
                onClick={() => setEmplacementExcel("TOUS")}
              >
                Les deux (2 feuilles)
              </button>
            </div>
            <button
              type="button"
              className="etiq-csv-btn"
              onClick={telechargerExcelGisements}
              disabled={excelGisLoading}
            >
              <HiDownload />{" "}
              {excelGisLoading ? "Export…" : "Télécharger l'Excel"}
            </button>
            <span className="etiq-hint">
              Une ligne par gisement : le <strong>code</strong>, le{" "}
              <strong>nombre d&apos;articles</strong> qui y sont rangés, puis le
              libellé du rayon.{" "}
              {emplacementExcel === "TOUS"
                ? "GISM1 (magasin) et GISM2 (dock) sur deux feuilles séparées."
                : excelSuitSelection
                  ? `Limité aux ${selectedGisQr.length} gisement(s) sélectionné(s) ci-dessus.`
                  : `Tous les gisements ${emplacementExcel === "DOCK" ? "GISM2" : "GISM1"} de la société.`}{" "}
              Les articles <strong>sans gisement</strong> n&apos;y figurent pas.
            </span>
          </div>
        </div>
      )}

      {/* Source des articles (le type « custom » ajoute « Texte seul »). */}
      {type !== "qr_gisement" && (
      <div className="etiq-card">
        <div className="etiq-mode-tabs">
          {type === "custom" && (
            <button
              type="button"
              className={`etiq-mode-btn ${mode === "aucun" ? "active" : ""}`}
              onClick={() => setMode("aucun")}
            >
              <HiPencilAlt /> Texte seul
            </button>
          )}
          {type === "custom" && (
            <button
              type="button"
              className={`etiq-mode-btn ${mode === "import" ? "active" : ""}`}
              onClick={() => setMode("import")}
            >
              <HiTable /> Importer un fichier (masse)
            </button>
          )}
          <button
            type="button"
            className={`etiq-mode-btn ${mode === "proforma" ? "active" : ""}`}
            onClick={() => setMode("proforma")}
          >
            <HiDocumentText /> N° de proforma
          </button>
          <button
            type="button"
            className={`etiq-mode-btn ${mode === "commande" ? "active" : ""}`}
            onClick={() => setMode("commande")}
          >
            <HiTruck /> N° de commande
          </button>
          <button
            type="button"
            className={`etiq-mode-btn ${mode === "nart" ? "active" : ""}`}
            onClick={() => setMode("nart")}
          >
            <HiPencilAlt /> Saisie manuelle (NART)
          </button>
          <button
            type="button"
            className={`etiq-mode-btn ${mode === "gism1" ? "active" : ""}`}
            onClick={() => setMode("gism1")}
          >
            <HiLocationMarker /> Par gisement (GISM1)
          </button>
          <button
            type="button"
            className={`etiq-mode-btn ${mode === "groupe" ? "active" : ""}`}
            onClick={() => setMode("groupe")}
          >
            <HiCollection /> Par groupe (GROUPE)
          </button>
        </div>

        {mode === "proforma" && (
          <div className="etiq-field">
            <label className="etiq-label">Numéro de proforma</label>
            <input
              type="text"
              className="etiq-input"
              value={numfact}
              onChange={(e) => setNumfact(e.target.value)}
              placeholder="Ex. 0001234"
              autoCapitalize="characters"
            />
            <span className="etiq-hint">
              Les articles sont récupérés et triés par numéro de ligne (NL).
              Chaque référence est tirée <strong>autant de fois que sa quantité</strong>{" "}
              sur la proforma, et l&apos;<strong>observation</strong> du document (champ
              « Texte / Objet ») est rappelée en haut de feuille et en marge de
              chaque rangée — rien n&apos;est écrit si elle est vide.
            </span>
          </div>
        )}

        {mode === "commande" && (
          <div className="etiq-field">
            <label className="etiq-label">Numéro de commande</label>
            <input
              type="text"
              className="etiq-input"
              value={numcde}
              onChange={(e) => setNumcde(e.target.value)}
              placeholder="Ex. CMD0001234"
              autoCapitalize="characters"
            />
            <span className="etiq-hint">
              Les articles de la commande sont récupérés et triés par numéro de
              ligne (NL).
            </span>
          </div>
        )}

        {mode === "nart" && (
          <div className="etiq-field">
            <label className="etiq-label">Liste de NART</label>
            <div className="etiq-csv-row">
              <button
                type="button"
                className="etiq-csv-btn"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <HiUpload /> Importer un CSV
              </button>
              {csvCount > 0 && (
                <span className="etiq-csv-count">
                  {csvCount} NART chargé(s)
                </span>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={handleCsvImport}
                style={{ display: "none" }}
              />
            </div>
            <textarea
              className="etiq-textarea"
              rows={5}
              value={nartText}
              onChange={(e) => { setNartText(e.target.value); setCsvCount(0); }}
              placeholder={"Un NART par ligne (ou séparés par , ou ;)"}
            />
            <span className="etiq-hint">
              Les NART sont traités dans l'ordre. L'import CSV détecte le
              séparateur automatiquement et prend la première colonne.
            </span>
          </div>
        )}

        {mode === "gism1" && (
          <div className="etiq-field">
            <label className="etiq-label">Gisement(s) GISM1</label>
            <SelecteurMultiple
              key={`gism1-${nomDossierDBF || ""}`}
              items={gism1Data?.gism1}
              selected={selectedGism1}
              onToggle={toggleGism1}
              onClear={() => setSelectedGism1([])}
              loading={loadingGism1}
              placeholder="Rechercher et sélectionner un ou plusieurs gisements…"
            />
            <span className="etiq-hint">
              {selectedGism1.length > 0
                ? `${selectedGism1.length} gisement(s) sélectionné(s). Toutes les étiquettes des articles rangés dans ce(s) gisement(s) seront générées.`
                : "Toutes les étiquettes des articles rangés dans le(s) gisement(s) sélectionné(s) seront générées."}
            </span>
          </div>
        )}

        {mode === "groupe" && (
          <div className="etiq-field">
            <label className="etiq-label">Groupe(s) / Famille(s)</label>
            <SelecteurMultiple
              key={`groupe-${nomDossierDBF || ""}`}
              items={groupeData?.groupes}
              selected={selectedGroupe}
              onToggle={toggleGroupe}
              onClear={() => setSelectedGroupe([])}
              loading={loadingGroupe}
              placeholder="Rechercher et sélectionner un ou plusieurs groupes…"
            />
            <span className="etiq-hint">
              {selectedGroupe.length > 0
                ? `${selectedGroupe.length} groupe(s) sélectionné(s). Toutes les étiquettes des articles de ce(s) groupe(s) seront générées.`
                : "Toutes les étiquettes des articles du/des groupe(s) sélectionné(s) seront générées."}
            </span>
          </div>
        )}

        {type === "custom" && mode === "import" && (
          <div className="etiq-field">
            <label className="etiq-label">Fichier de données (Excel / CSV)</label>
            <div className="etiq-csv-row">
              <button
                type="button"
                className="etiq-csv-btn"
                onClick={() => importFileRef.current && importFileRef.current.click()}
              >
                <HiUpload /> Importer un fichier
              </button>
              {importRows.length > 0 && (
                <>
                  <span className="etiq-csv-count">
                    {importRows.length} ligne{importRows.length > 1 ? "s" : ""} · {importCols} colonne{importCols > 1 ? "s" : ""}
                  </span>
                  <button
                    type="button"
                    className="etiq-mode-btn"
                    onClick={clearImport}
                    title="Retirer le fichier"
                  >
                    <HiX /> Retirer
                  </button>
                </>
              )}
              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleImportFile}
                style={{ display: "none" }}
              />
            </div>

            {importError && <div className="etiq-error">{importError}</div>}

            {importRows.length > 0 && (
              <>
                <span className="etiq-hint">
                  Le fichier ne doit <b>pas</b> avoir de ligne d'en-tête. Chaque
                  ligne produira une étiquette. Placez les colonnes détectées
                  (« Colonne 1 … {importCols} ») sur l'étiquette ci-dessous, comme
                  des champs article.
                </span>
                <div className="etiq-import-preview">
                  <table>
                    <thead>
                      <tr>
                        {importFields.map((f) => (
                          <th key={f.key}>{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.slice(0, 5).map((r, ri) => (
                        <tr key={ri}>
                          {importFields.map((f, ci) => (
                            <td key={f.key}>{r[ci] ?? ""}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importRows.length > 5 && (
                    <div className="etiq-import-more">
                      … et {importRows.length - 5} ligne(s) de plus
                    </div>
                  )}
                </div>
              </>
            )}

            {importRows.length === 0 && !importError && (
              <span className="etiq-hint">
                Importez un fichier <b>sans en-tête</b> (une ou plusieurs colonnes).
                Le module détecte le nombre de colonnes ; chaque ligne devient une
                étiquette générée en masse.
              </span>
            )}
          </div>
        )}

        {type === "custom" && mode === "aucun" && (
          <span className="etiq-hint">
            Mode « texte seul » : l'étiquette ne dépend d'aucun article. Choisissez
            une source ci-dessus pour insérer des champs article + code-barres.
          </span>
        )}
      </div>
      )}

      {/* Type d'étiquette */}
      <div className="etiq-card">
        <label className="etiq-label">Type d'étiquette</label>
        <div className="etiq-types">
          {LABEL_TYPES.map((lt) => (
            <button
              type="button"
              key={lt.type}
              className={`etiq-type-card ${type === lt.type ? "selected" : ""}`}
              onClick={() => {
                setType(lt.type);
                // Custom démarre en « texte seul » ; sortir de custom rétablit une
                // source article (les modes « aucun »/« import » n'existent qu'en custom).
                if (lt.type === "custom") setMode("aucun");
                else if (mode === "aucun" || mode === "import") setMode("nart");
              }}
            >
              <span className="etiq-type-title">{lt.title}</span>
              <span className="etiq-type-desc">{lt.desc}</span>
            </button>
          ))}
        </div>

        {/* Le choix A4 / demi A4 ne concerne que les types pleine page : les
            grilles « standard* » tuilent déjà 20 étiquettes par feuille. */}
        {!type.startsWith("standard") &&
          type !== "custom" &&
          type !== "qr_gisement" && (
          <div className="etiq-format">
            <span className="etiq-format-label">Format :</span>
            <button
              type="button"
              className={`etiq-mode-btn ${format === "a4" ? "active" : ""}`}
              onClick={() => setFormat("a4")}
            >
              A4 entier (1 / page)
            </button>
            <button
              type="button"
              className={`etiq-mode-btn ${format === "demi" ? "active" : ""}`}
              onClick={() => setFormat("demi")}
            >
              Demi A4 (2 / page)
            </button>
          </div>
        )}
      </div>

      {/* Designer d'étiquette personnalisée */}
      {type === "custom" && (
        <div className="etiq-card">
          <label className="etiq-label">Conception de l'étiquette</label>
          <CustomEtiquetteDesigner
            key={designerKey}
            initial={templateInitial}
            dataMode={mode !== "aucun"}
            fields={mode === "import" ? importFields : undefined}
            onChange={setCustomLayout}
          />
        </div>
      )}

      {/* Templates de la société (enregistrer / réutiliser) */}
      {type === "custom" && (
        <div className="etiq-card">
          <label className="etiq-label">
            <HiBookmark /> Mes templates de la société
          </label>
          <span className="etiq-hint">
            Enregistrez ce design pour le réutiliser plus tard. Tous les utilisateurs
            ayant accès à cette société et au module étiquettes le retrouveront ici.
          </span>

          {tplMsg && (
            <div className={tplMsg.type === "ok" ? "etiq-info" : "etiq-error"}>
              {tplMsg.text}
            </div>
          )}

          <div className="etiq-tpl-save">
            <input
              type="text"
              className="etiq-input"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Nom du template (ex. Étiquette rayon promo)"
            />
            <button
              type="button"
              className="etiq-tpl-savebtn"
              onClick={saveTemplate}
              disabled={savingTpl}
            >
              <HiSave /> {savingTpl ? "Enregistrement…" : "Enregistrer ce design"}
            </button>
          </div>

          {templates.length === 0 ? (
            <div className="etiq-hint">Aucun template pour cette société pour l'instant.</div>
          ) : (
            <div className="etiq-tpl-list">
              {templates.map((tpl) => (
                <div key={tpl._id} className="etiq-tpl-item">
                  <div className="etiq-tpl-info">
                    <span className="etiq-tpl-name">{tpl.nom}</span>
                    <span className="etiq-tpl-meta">
                      {tpl.dataMode ? "Avec données article" : "Texte seul"}
                      {tpl.user?.prenom || tpl.user?.nom
                        ? ` · par ${[tpl.user.prenom, tpl.user.nom].filter(Boolean).join(" ")}`
                        : ""}
                    </span>
                  </div>
                  <div className="etiq-tpl-actions">
                    <button type="button" className="etiq-tpl-use" onClick={() => loadTemplate(tpl)}>
                      Utiliser
                    </button>
                    <button type="button" className="etiq-tpl-del" onClick={() => removeTemplate(tpl)} title="Supprimer">
                      <HiTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <div className="etiq-error">{error}</div>}
      {info && <div className="etiq-info">{info}</div>}

      <div className="etiq-actions">
        <button
          type="button"
          className="etiq-generate"
          onClick={genererEtiquettes}
          disabled={loading}
        >
          {loading
            ? "Génération…"
            : type === "qr_gisement"
              ? "📍 Générer les panneaux QR"
              : "🏷️ Générer les étiquettes"}
        </button>
      </div>

      {/* Articles sans code-barres : la question n'est posée QUE s'il y en a,
          et une seule fois, juste avant la génération. */}
      {controleGencod && (
        <div
          className="etiq-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="etiq-modal-titre"
        >
          <div className="etiq-modal">
            <div className="etiq-modal-head">
              <h3 id="etiq-modal-titre">
                {controleGencod.nbSansGencod} article
                {controleGencod.nbSansGencod > 1 ? "s" : ""} sans code-barres
              </h3>
              <button
                type="button"
                className="etiq-modal-close"
                onClick={() => setControleGencod(null)}
                title="Annuler"
              >
                <HiX />
              </button>
            </div>

            <p className="etiq-modal-text">
              Ces articles n&apos;ont pas de GENCOD exploitable en base : leur
              étiquette ne portera aucun code-barres. Que voulez-vous mettre à la
              place&nbsp;? «&nbsp;Laisser vide&nbsp;» retire aussi le petit NART
              du coin : l&apos;étiquette ne garde que la désignation et le prix.
            </p>

            <div className="etiq-modal-liste">
              {(controleGencod.articles || []).slice(0, 12).map((art) => (
                <div key={art.NART} className="etiq-modal-ligne">
                  <span className="etiq-modal-nart">{art.NART}</span>
                  <span className="etiq-modal-design">{art.DESIGN}</span>
                </div>
              ))}
              {controleGencod.nbSansGencod > 12 && (
                <div className="etiq-modal-more">
                  … et {controleGencod.nbSansGencod - 12} autre
                  {controleGencod.nbSansGencod - 12 > 1 ? "s" : ""}
                </div>
              )}
            </div>

            <div className="etiq-modal-actions">
              <button
                type="button"
                className="etiq-modal-btn etiq-modal-btn--primary"
                onClick={() => lancerGeneration(controleGencod.body, "nart")}
              >
                Afficher le NART à la place
              </button>
              <button
                type="button"
                className="etiq-modal-btn"
                onClick={() => lancerGeneration(controleGencod.body, "vide")}
              >
                Laisser vide (aucun NART)
              </button>
            </div>
            <div className="etiq-modal-hint">
              Les {controleGencod.total} étiquette(s) sont générées dans les deux
              cas ; seuls les articles ci-dessus changent.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEtiquettesScreen;